import {
  Checklist,
  ChecklistExemption,
  Intervention,
  Job,
  Objective,
  ObjectiveAssignment,
  Task,
} from "protos/rtc/jobs";
import {
  createSelector,
  createSlice,
  Draft,
  PayloadAction,
} from "@reduxjs/toolkit";

type ObjectiveId = number;
type TaskId = number;
type JobId = number;
type RobotSerial = string;

interface State {
  // entity storage
  jobs: Record<JobId, Job>;
  objectives: Record<ObjectiveId, Objective | undefined>;
  tasks: Record<TaskId, Task>;
  // per robot
  assignmentsBySerial: Record<RobotSerial, ObjectiveAssignment[]>;
  activeObjectiveBySerial: Record<RobotSerial, Objective | undefined>;
  activeTaskBySerial: Record<RobotSerial, Task | undefined>;
  checklistBySerial: Record<RobotSerial, Checklist | undefined>;
  checklistExemptionsBySerial: Record<
    RobotSerial,
    ChecklistExemption | undefined
  >;
  interventionsBySerial: Record<RobotSerial, Intervention[]>;
  interventions: Intervention[];
  // per objective
  assignmentsByObjectiveId: Record<number, ObjectiveAssignment>;
  tasksByObjectiveId: Record<ObjectiveId, Task[]>;
}

const initialState: State = {
  // top level
  objectives: {},
  jobs: {},
  tasks: {},
  // per robot
  assignmentsBySerial: {},
  activeObjectiveBySerial: {},
  activeTaskBySerial: {},
  checklistBySerial: {},
  checklistExemptionsBySerial: {},
  interventionsBySerial: {},
  interventions: [],
  // per objective
  assignmentsByObjectiveId: {},
  tasksByObjectiveId: {},
};

// Filter one bucket of a Record<string, T[]> by predicate.
function filterBucket<T>(
  record: Record<string, T[]>,
  key: string,
  predicate: (item: T) => boolean
): void {
  const bucket = record[key];
  if (bucket) {
    record[key] = bucket.filter((x) => predicate(x));
  }
}

// Filter every bucket of a Record<string, T[]> by predicate.
function filterAllBuckets<T>(
  record: Record<string, T[]>,
  predicate: (item: T) => boolean
): void {
  for (const key of Object.keys(record)) {
    filterBucket(record, key, predicate);
  }
}

// Upsert by id into an immer draft array
function upsertById<T extends { id: number }>(
  arr: Draft<T>[],
  item: Draft<T>
): void {
  const idx = arr.findIndex((x) => x.id === item.id);
  if (idx >= 0) {
    arr[idx] = item;
  } else {
    arr.push(item);
  }
}

// Propagate an objective update to both the entity store and any robot's
// active objective that references the same id.
function syncObjective(state: Draft<State>, objective: Objective): void {
  state.objectives[objective.id] = objective;
  for (const serial of Object.keys(state.activeObjectiveBySerial)) {
    if (state.activeObjectiveBySerial[serial]?.id === objective.id) {
      state.activeObjectiveBySerial[serial] = objective;
    }
  }
}

// Remove an objective from the entity store and clear it from any robot that
// has it as their active objective.
function removeObjective(state: Draft<State>, objectiveId: number): void {
  state.objectives[objectiveId] = undefined;
  for (const serial of Object.keys(state.activeObjectiveBySerial)) {
    if (state.activeObjectiveBySerial[serial]?.id === objectiveId) {
      state.activeObjectiveBySerial[serial] = undefined;
    }
  }
}

// Propagate a task update to the entity store and any robot's active task.
function syncTask(state: Draft<State>, task: Task): void {
  state.tasks[task.id] = task;
  for (const serial of Object.keys(state.activeTaskBySerial)) {
    if (state.activeTaskBySerial[serial]?.id === task.id) {
      state.activeTaskBySerial[serial] = task;
    }
  }
  const bucket = state.tasksByObjectiveId[task.objectiveId] ?? [];
  upsertById(bucket, task);
  state.tasksByObjectiveId[task.objectiveId] = bucket;
}

// Remove a task from the entity store and clear it from any robot's active task.
function removeTask(state: Draft<State>, taskId: number): void {
  const objId = state.tasks[taskId]?.objectiveId;
  delete state.tasks[taskId];
  for (const serial of Object.keys(state.activeTaskBySerial)) {
    if (state.activeTaskBySerial[serial]?.id === taskId) {
      state.activeTaskBySerial[serial] = undefined;
    }
  }
  if (objId !== undefined) {
    filterBucket(
      state.tasksByObjectiveId,
      String(objId),
      (x) => x.id !== taskId
    );
  }
}

// Keep interventionsBySerial and the global interventions array in sync.
// Uses intervention.robotSerial as the bucket key.
function syncIntervention(
  state: Draft<State>,
  intervention: Intervention
): void {
  const serial = intervention.robotSerial;
  if (!state.interventionsBySerial[serial]) {
    state.interventionsBySerial[serial] = [];
  }
  upsertById(state.interventionsBySerial[serial], intervention);
  upsertById(state.interventions, intervention);
}

// Keep assignmentsBySerial and assignmentsByObjectiveId in sync.
function syncAssignment(
  state: Draft<State>,
  assignment: ObjectiveAssignment
): void {
  const existing = state.assignmentsBySerial[assignment.robotSerial] ?? [];
  const idx = existing.findIndex(
    (x) => x.objectiveId === assignment.objectiveId
  );
  if (idx >= 0) {
    existing[idx] = assignment;
  } else {
    existing.push(assignment);
  }
  state.assignmentsBySerial[assignment.robotSerial] = existing;
  state.assignmentsByObjectiveId[assignment.objectiveId] = assignment;
}

// Remove all assignments for a serial, or a single assignment by objectiveId.
function removeAssignment(
  state: Draft<State>,
  by: { objectiveId: number } | { serial: RobotSerial }
): void {
  if ("serial" in by) {
    const assignments = state.assignmentsBySerial[by.serial] ?? [];
    for (const a of assignments) {
      delete state.assignmentsByObjectiveId[a.objectiveId];
    }
    delete state.assignmentsBySerial[by.serial];
  } else {
    const assignment = state.assignmentsByObjectiveId[by.objectiveId];
    if (!assignment) {
      return;
    }
    filterBucket(
      state.assignmentsBySerial,
      assignment.robotSerial,
      (x) => x.objectiveId !== by.objectiveId
    );
    delete state.assignmentsByObjectiveId[by.objectiveId];
  }
}

// Remove an intervention from both stores by id.
function removeIntervention(state: Draft<State>, interventionId: number): void {
  state.interventions = state.interventions.filter(
    (x) => x.id !== interventionId
  );
  filterAllBuckets(state.interventionsBySerial, (x) => x.id !== interventionId);
}

// Minimal root state shape needed to use these selectors in common hooks.
export type AtkJobDataRootState = { atkJobData: typeof initialState };

export const atkJobData = createSlice({
  name: "atkJobData",
  initialState,
  reducers: {
    // top level
    setJob(state, action: PayloadAction<{ job: Job }>) {
      state.jobs[action.payload.job.id] = action.payload.job;
    },
    // NOTE: clearJob is intentionally non-cascading. The server emits
    // per-entity delete events (clearObjective, clearTask, clearAssignment),
    // so cascading here would risk double-removal and orphan handling.
    clearJob(state, action: PayloadAction<{ jobId: number }>) {
      delete state.jobs[action.payload.jobId];
    },
    setObjective(state, action: PayloadAction<{ objective: Objective }>) {
      syncObjective(state, action.payload.objective);
    },
    clearObjective(state, action: PayloadAction<{ objectiveId: ObjectiveId }>) {
      removeObjective(state, action.payload.objectiveId);
    },
    setTask(state, action: PayloadAction<{ task: Task }>) {
      syncTask(state, action.payload.task);
    },
    clearTask(state, action: PayloadAction<{ taskId: number }>) {
      removeTask(state, action.payload.taskId);
    },
    // per robot
    setActiveObjective(
      state,
      action: PayloadAction<{ serial: RobotSerial; objective: Objective }>
    ) {
      const { serial, objective } = action.payload;
      syncObjective(state, objective);
      state.activeObjectiveBySerial[serial] = objective;
    },
    clearActiveObjective(
      state,
      action: PayloadAction<{ serial: RobotSerial }>
    ) {
      state.activeObjectiveBySerial[action.payload.serial] = undefined;
    },
    setActiveTask(
      state,
      action: PayloadAction<{ serial: RobotSerial; task: Task }>
    ) {
      const { serial, task } = action.payload;
      syncTask(state, task);
      state.activeTaskBySerial[serial] = task;
    },
    clearActiveTask(state, action: PayloadAction<{ serial: RobotSerial }>) {
      state.activeTaskBySerial[action.payload.serial] = undefined;
    },
    setChecklist(
      state,
      action: PayloadAction<{ serial: RobotSerial; checklist: Checklist }>
    ) {
      state.checklistBySerial[action.payload.serial] = action.payload.checklist;
    },
    setChecklistExemption(
      state,
      action: PayloadAction<{
        serial: RobotSerial;
        checklistExemption: ChecklistExemption;
      }>
    ) {
      state.checklistExemptionsBySerial[action.payload.serial] =
        action.payload.checklistExemption;
    },
    clearChecklist(state, action: PayloadAction<{ serial: RobotSerial }>) {
      state.checklistBySerial[action.payload.serial] = undefined;
    },
    clearChecklistExemption(
      state,
      action: PayloadAction<{ serial: RobotSerial }>
    ) {
      state.checklistExemptionsBySerial[action.payload.serial] = undefined;
    },
    setIntervention(
      state,
      action: PayloadAction<{ serial: RobotSerial; intervention: Intervention }>
    ) {
      syncIntervention(state, action.payload.intervention);
    },
    clearIntervention(
      state,
      action: PayloadAction<{ serial: RobotSerial; interventionId: number }>
    ) {
      removeIntervention(state, action.payload.interventionId);
    },
    setGlobalIntervention(
      state,
      action: PayloadAction<{ intervention: Intervention }>
    ) {
      syncIntervention(state, action.payload.intervention);
    },
    seedGlobalInterventions(
      state,
      action: PayloadAction<{ interventions: Intervention[] }>
    ) {
      for (const intervention of action.payload.interventions) {
        syncIntervention(state, intervention);
      }
    },
    clearGlobalIntervention(
      state,
      action: PayloadAction<{ interventionId: number }>
    ) {
      removeIntervention(state, action.payload.interventionId);
    },
    // per objective
    setAssignment(
      state,
      action: PayloadAction<{ assignment: ObjectiveAssignment }>
    ) {
      syncAssignment(state, action.payload.assignment);
    },
    seedGlobalAssignments(
      state,
      action: PayloadAction<{ assignments: ObjectiveAssignment[] }>
    ) {
      for (const assignment of action.payload.assignments) {
        syncAssignment(state, assignment);
      }
    },
    clearAssignment(
      state,
      action: PayloadAction<{ objectiveId: number } | { serial: RobotSerial }>
    ) {
      removeAssignment(state, action.payload);
    },
  },
});

type S = AtkJobDataRootState;

// top level
export const selectJob = createSelector(
  (state: S) => state.atkJobData.jobs,
  (_: S, id: JobId) => id,
  (jobs, id) => jobs[id]
);
export const selectObjective = createSelector(
  (state: S) => state.atkJobData.objectives,
  (_: S, id: ObjectiveId) => id,
  (objectives, id) => objectives[id]
);
export const selectTask = createSelector(
  (state: S) => state.atkJobData.tasks,
  (_: S, id: TaskId) => id,
  (tasks, id) => tasks[id]
);

// per robot
export const selectActiveObjective = createSelector(
  (state: S) => state.atkJobData.activeObjectiveBySerial,
  (_: S, serial: RobotSerial) => serial,
  (map, serial) => map[serial]
);
export const selectActiveTask = createSelector(
  (state: S) => state.atkJobData.activeTaskBySerial,
  (_: S, serial: RobotSerial) => serial,
  (map, serial) => map[serial]
);
export const selectAssignmentsBySerial = createSelector(
  (state: S) => state.atkJobData.assignmentsBySerial,
  (_: S, serial: RobotSerial) => serial,
  (map, serial) => map[serial] ?? []
);
export const selectChecklistBySerial = createSelector(
  (state: S) => state.atkJobData.checklistBySerial,
  (_: S, serial: RobotSerial) => serial,
  (map, serial) => map[serial]
);
export const selectChecklistExemptionBySerial = createSelector(
  (state: S) => state.atkJobData.checklistExemptionsBySerial,
  (_: S, serial: RobotSerial) => serial,
  (map, serial) => map[serial]
);
export const selectChecklistDataBySerial = createSelector(
  (state: S) => state.atkJobData.checklistBySerial,
  (state: S) => state.atkJobData.checklistExemptionsBySerial,
  (_: S, serial: RobotSerial) => serial,
  (checklists, exemptions, serial) => ({
    checklist: checklists[serial],
    exemption: exemptions[serial],
  })
);
export const selectInterventionsBySerial = createSelector(
  (state: S) => state.atkJobData.interventionsBySerial,
  (_: S, serial: RobotSerial) => serial,
  (map, serial) => map[serial] ?? []
);
export const selectAllInterventions = (state: S): Intervention[] =>
  state.atkJobData.interventions;

// per objective
export const selectAssignmentByObjectiveId = createSelector(
  (state: S) => state.atkJobData.assignmentsByObjectiveId,
  (_: S, objectiveId: ObjectiveId) => objectiveId,
  (map, objectiveId) => map[objectiveId]
);
export const selectTasksByObjectiveId = createSelector(
  (state: S) => state.atkJobData.tasksByObjectiveId,
  (_: S, objectiveId: ObjectiveId) => objectiveId,
  (map, objectiveId) => map[objectiveId]
);
